import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { profileClient } from "@/shared/api/clients";
import { apiErrorMessage } from "@/shared/api/apiError";
import { ReportGenUser } from "../interfaces/User";
import { useToast } from "../toast";
import { queryClient } from "../api/queryClient";
import {
  stationSeatsQuery,
  stationUsersQuery,
  usersKeys,
} from "../../features/users/api/usersQueries";

export const useStationUsers = (organizationId: string) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [resendingInviteEmail, setResendingInviteEmail] = useState<string | null>(null);
  const [resettingPasswordEmail, setResettingPasswordEmail] = useState<string | null>(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusAction, setStatusAction] = useState<"activate" | "deactivate" | null>(null);
  const [selectedUser, setSelectedUser] = useState<ReportGenUser | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [badgeDialogUser, setBadgeDialogUser] = useState<ReportGenUser | null>(null);
  const [isSavingBadge, setIsSavingBadge] = useState(false);
  const [roleDialogUser, setRoleDialogUser] = useState<ReportGenUser | null>(null);
  const [isSavingRole, setIsSavingRole] = useState(false);

  const toast = useToast();

  const usersQuery = useQuery({
    ...stationUsersQuery(organizationId),
    enabled: !!organizationId,
  });
  // Seats come from their own endpoint (the users list is third-party
  // consumed and contract-stable). A seats failure just hides the card —
  // seats stays null and the roster renders regardless.
  const seatsQuery = useQuery({
    ...stationSeatsQuery(organizationId),
    enabled: !!organizationId,
  });
  const users = usersQuery.data?.users ?? [];
  const seats = seatsQuery.data ?? null;
  // Lets the panel reserve the Seats card's slot instead of reflowing the
  // stat row when the (slower) seats query settles after the roster.
  const isSeatsLoading = seatsQuery.isLoading;
  const lookupFailures = usersQuery.data?.lookupFailures ?? 0;
  // isFetching keeps the loading state visible during explicit refreshes,
  // matching the old fetchAllUsers behavior.
  const isLoading = (!!organizationId && usersQuery.isPending) || usersQuery.isFetching;

  // With no data the panel renders a persistent error state instead; the
  // toast only covers a failed refresh of an already-visible roster. Keying
  // on errorUpdatedAt (not the error object) and skipping while a refetch is
  // in flight stops the cached error from re-toasting on tab remount.
  const hasData = usersQuery.data !== undefined;
  useEffect(() => {
    if (!usersQuery.isError || usersQuery.isFetching || !hasData) return;
    toast.error(apiErrorMessage(usersQuery.error, "Failed to refresh users"));
  }, [
    usersQuery.errorUpdatedAt,
    usersQuery.isError,
    usersQuery.isFetching,
    usersQuery.error,
    hasData,
    toast,
  ]);

  const loadFailed = usersQuery.isError && !usersQuery.isFetching && !hasData;
  const loadErrorMessage = loadFailed
    ? apiErrorMessage(usersQuery.error, "Failed to load users")
    : null;

  // Invalidates both roster and directory variants for this station.
  const fetchAllUsers = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: usersKeys.station(organizationId),
    });
  }, [organizationId]);

  const handleResendInvite = useCallback(
    async (user: ReportGenUser) => {
      setResendingInviteEmail(user.email);
      try {
        if (user.id) {
          await profileClient.post(
            `/v1/stations/${organizationId}/users/${user.id}/invite`,
            { userId: user.id },
          );
        } else {
          await profileClient.post(
            `/v1/stations/${organizationId}/users/invite`,
            { email: user.email },
          );
        }
        toast.success(`Invitation resent to ${user.email}`);
      } catch (error) {
        toast.error(apiErrorMessage(error, "Failed to resend invitation"));
      } finally {
        setResendingInviteEmail(null);
      }
    },
    [organizationId, toast],
  );

  const handleResetPassword = useCallback(
    async (user: ReportGenUser) => {
      if (!user.id) {
        toast.error(
          `${user.email} hasn't accepted their invitation yet, so there's no password to reset. Use "Resend email invite" instead.`,
        );
        return;
      }
      setResettingPasswordEmail(user.email);
      try {
        await profileClient.post(
          `/v1/stations/${organizationId}/users/${user.id}/password-reset`,
        );
        toast.success(`Password reset email sent to ${user.email}`);
      } catch (error) {
        toast.error(apiErrorMessage(error, "Failed to send password reset"));
      } finally {
        setResettingPasswordEmail(null);
      }
    },
    [organizationId, toast],
  );

  const handleStatusChangeRequest = useCallback(
    (user: ReportGenUser, action: "activate" | "deactivate") => {
      setSelectedUser(user);
      setStatusAction(action);
      setStatusDialogOpen(true);
    },
    [],
  );

  const closeStatusDialog = useCallback(() => {
    if (isUpdatingStatus) return;
    setStatusDialogOpen(false);
    setStatusAction(null);
    setSelectedUser(null);
  }, [isUpdatingStatus]);

  const handleStatusConfirm = useCallback(async () => {
    if (!selectedUser || !statusAction) return;
    setIsUpdatingStatus(true);
    try {
      const payload = { email: selectedUser.email };
      if (statusAction === "activate") {
        await profileClient.post(
          `/v1/stations/${organizationId}/users/${selectedUser.id}/activate`,
          payload,
        );
        toast.success("User re-activated successfully.");
      } else {
        await profileClient.post(
          `/v1/stations/${organizationId}/users/${selectedUser.id}/deactivate`,
          payload,
        );
        toast.success("User deactivated successfully.");
      }
      await fetchAllUsers();
      setStatusDialogOpen(false);
      setStatusAction(null);
      setSelectedUser(null);
    } catch (error) {
      // Keep the dialog open so the admin can retry or cancel in context.
      toast.error(apiErrorMessage(error, "Failed to update user status."));
    } finally {
      setIsUpdatingStatus(false);
    }
  }, [selectedUser, statusAction, organizationId, toast, fetchAllUsers]);

  const openBadgeDialog = useCallback((user: ReportGenUser) => {
    setBadgeDialogUser(user);
  }, []);

  const closeBadgeDialog = useCallback(() => {
    if (isSavingBadge) return;
    setBadgeDialogUser(null);
  }, [isSavingBadge]);

  const handleBadgeSave = useCallback(
    async (badgeNumber: string) => {
      if (!badgeDialogUser?.id) return;
      setIsSavingBadge(true);
      try {
        await profileClient.patch(
          `/v1/stations/${organizationId}/users/${badgeDialogUser.id}`,
          { badgeNumber },
        );
        toast.success(`Badge number updated for ${badgeDialogUser.email}`);
        await fetchAllUsers();
        setBadgeDialogUser(null);
      } catch (error) {
        // Keep the dialog open so the admin can correct and retry.
        toast.error(apiErrorMessage(error, "Failed to update badge number."));
      } finally {
        setIsSavingBadge(false);
      }
    },
    [badgeDialogUser, organizationId, toast, fetchAllUsers],
  );

  const openRoleDialog = useCallback((user: ReportGenUser) => {
    setRoleDialogUser(user);
  }, []);

  const closeRoleDialog = useCallback(() => {
    if (isSavingRole) return;
    setRoleDialogUser(null);
  }, [isSavingRole]);

  const handleRoleSave = useCallback(
    async (roleSlug: string) => {
      if (!roleDialogUser?.id) return;
      setIsSavingRole(true);
      try {
        await profileClient.patch(
          `/v1/stations/${organizationId}/users/${roleDialogUser.id}`,
          { roleSlug },
        );
        toast.success(`Role updated for ${roleDialogUser.email}`);
        await fetchAllUsers();
        setRoleDialogUser(null);
      } catch (error) {
        // Keep the dialog open so the admin can correct and retry.
        toast.error(apiErrorMessage(error, "Failed to update role."));
      } finally {
        setIsSavingRole(false);
      }
    },
    [roleDialogUser, organizationId, toast, fetchAllUsers],
  );

  const statusUpdatingEmail = isUpdatingStatus && selectedUser ? selectedUser.email : null;

  return {
    users,
    seats,
    isSeatsLoading,
    lookupFailures,
    isLoading,
    loadFailed,
    loadErrorMessage,
    searchTerm,
    setSearchTerm,
    resendingInviteEmail,
    resettingPasswordEmail,
    statusUpdatingEmail,
    statusDialogOpen,
    statusAction,
    selectedUser,
    isUpdatingStatus,
    refresh: fetchAllUsers,
    handleResendInvite,
    handleResetPassword,
    handleStatusChangeRequest,
    closeStatusDialog,
    handleStatusConfirm,
    badgeDialogUser,
    isSavingBadge,
    openBadgeDialog,
    closeBadgeDialog,
    handleBadgeSave,
    roleDialogUser,
    isSavingRole,
    openRoleDialog,
    closeRoleDialog,
    handleRoleSave,
  };
};
